import React from 'react';
import { useTranslation } from 'react-i18next';
import { DataProvider } from '@qualio/ui-components';
import { EventStep, UserTask } from '../../../types';
import StepHeader from './StepHeader';
import StepBody from './StepBody';
import TaskStepMetadata from './TaskStepMetadata';
import StepTasksListV2 from './StepTasksListV2';
import EmptyStep from './EmptyStep';

export type TaskContentProps = {
  step: EventStep;
  stepStatus: EventStep['status'];
  stepNotCompleted: boolean;
  isEventOwner: boolean;
  onCompleteTask: (task: UserTask) => void;
  onReopenTask: (task: UserTask) => void;
  onOpenTask: (task: UserTask) => void;
  createTaskButton: React.ReactNode;
  completeStepButton: React.ReactNode;
  revertStepButton: React.ReactNode;
};

const TaskContent: React.FC<TaskContentProps> = ({
  step,
  stepStatus,
  stepNotCompleted,
  isEventOwner,
  onCompleteTask,
  onReopenTask,
  onOpenTask,
  createTaskButton,
  completeStepButton,
  revertStepButton,
}) => {
  const { t } = useTranslation();
  const { data: tasks, isLoading } = DataProvider.useDataProvider<UserTask>();
  const anyTaskCreated = !!tasks && tasks.length > 0;
  const openCount = tasks?.filter((task) => task.status === 'open').length ?? 0;
  const doneCount = tasks?.filter((task) => task.status === 'closed_success').length ?? 0;
  const closedCount = tasks?.filter((task) => task.status === 'closed_fail').length ?? 0;

  return (
    <>
      <StepHeader
        title={step.label}
        status={stepStatus}
        createTaskButton={anyTaskCreated && createTaskButton}
        completeStepButton={completeStepButton}
        revertStepButton={revertStepButton}
      />
      <StepBody isLoading={isLoading ?? false}>
        <TaskStepMetadata openCount={openCount} doneCount={doneCount} closedCount={closedCount} />
        {anyTaskCreated ? (
          <StepTasksListV2
            onCompleteTask={onCompleteTask}
            onReopenTask={onReopenTask}
            onOpenTask={onOpenTask}
            isEventOwner={isEventOwner}
            stepStatus={stepStatus}
          />
        ) : (
          <EmptyStep
            text={
              stepNotCompleted
                ? t('tasks.messages.createAndAssignTasks')
                : t('tasks.messages.noTasksAssociatedWithStep')
            }
          >
            {stepNotCompleted && createTaskButton}
          </EmptyStep>
        )}
      </StepBody>
    </>
  );
};

export default TaskContent;
